import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpRightIcon, HelpCircleIcon } from '@untitledui-icons/react/line'

import { Modal, ModalBody, ModalContent, ModalHeader } from '@/components/ui/Modal'
import { createTriggerSnippet } from './widgetConfig.preview'

/** Stand-in for a real assistant id, which is what a venue id is. */
const EXAMPLE_VENUE_ID = '42'

interface GuideStepProps {
  number: number
  title: string
  children: React.ReactNode
}

/** One numbered step of the main Hub flow: connect, assign, paste. */
function GuideStep({ number, title, children }: GuideStepProps) {
  return (
    <li className="flex gap-3">
      <span className="bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
        {number}
      </span>
      <div className="min-w-0 space-y-1">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">{children}</p>
      </div>
    </li>
  )
}

interface AdvancedOptionProps {
  title: string
  children: React.ReactNode
}

/** Optional custom-trigger setups, kept apart from the default flow. */
function AdvancedOption({ title, children }: AdvancedOptionProps) {
  return (
    <div className="space-y-2.5 rounded-lg border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-900/40">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {children}
    </div>
  )
}

function GuideCodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-lg bg-gray-100 p-3 font-mono text-xs text-gray-700 dark:bg-gray-950 dark:text-gray-300">
      {children}
    </pre>
  )
}

function InlineCode({ children }: { children: string }) {
  return (
    <code className="rounded bg-gray-200 px-1 py-0.5 font-mono text-[0.9em] text-gray-700 dark:bg-gray-800 dark:text-gray-300">
      {children}
    </code>
  )
}

interface HelpMenuRowProps {
  title: string
  description: string
  href?: string
  onAction?: () => void
}

const HELP_ROW_CLASS =
  'mx-1.5 my-px flex w-[calc(100%-12px)] cursor-pointer items-start gap-3 rounded-[6px] px-2.5 py-2 text-left hover:bg-[#fafaf9] dark:hover:bg-gray-800'

function HelpMenuRow({ title, description, href, onAction }: HelpMenuRowProps) {
  const content = (
    <>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-utility-gray-700 block truncate text-sm leading-5 font-semibold dark:text-gray-200">
          {title}
        </span>
        <span className="block truncate text-[13px] leading-[18px] text-[#6a6a6a] dark:text-gray-400">
          {description}
        </span>
      </span>
      <ArrowUpRightIcon className="text-utility-gray-700 mt-0.5 size-[18px] shrink-0 dark:text-gray-300" />
    </>
  )

  if (href) {
    return (
      <a
        role="menuitem"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={HELP_ROW_CLASS}
      >
        {content}
      </a>
    )
  }

  return (
    <button type="button" role="menuitem" onClick={onAction} className={HELP_ROW_CLASS}>
      {content}
    </button>
  )
}

function envUrl(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

/** Need-help control: Figma menu first, installation guide behind the first item. */
export function WidgetInstallationGuide() {
  const { t } = useTranslation()
  const guide = 'resources.widgets.guide'
  const help = 'resources.widgets.help_menu'
  const [menuOpen, setMenuOpen] = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const howItWorksUrl =
    envUrl(import.meta.env.VITE_WHATSAPP_WALKTHROUGH_URL) ??
    envUrl(import.meta.env.VITE_WHATSAPP_FAQ_URL)
  const calendlyUrl = envUrl(import.meta.env.VITE_WHATSAPP_CALENDLY_URL)

  useEffect(() => {
    if (!menuOpen) return

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  return (
    <>
      <div ref={rootRef} className="relative">
        <button
          type="button"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          onClick={() => setMenuOpen((open) => !open)}
          className="shadow-xs-skeumorphic inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm leading-5 font-semibold text-secondary ring-1 ring-gray-200 ring-inset outline-none hover:bg-[#fafaf9] focus-visible:ring-2 focus-visible:ring-gray-300 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-800"
        >
          <HelpCircleIcon className="size-5 shrink-0" />
          <span className="px-0.5">{t('resources.widgets.hub.need_help')}</span>
        </button>

        {menuOpen ? (
          <div
            role="menu"
            aria-label={t('resources.widgets.hub.need_help')}
            className="absolute top-[calc(100%+6px)] right-0 z-50 w-[288px] overflow-hidden rounded-md bg-white py-1 shadow-lg ring-1 ring-black/8 dark:bg-gray-900"
          >
            <HelpMenuRow
              title={t(`${help}.installation_title`)}
              description={t(`${help}.installation_description`)}
              onAction={() => {
                setMenuOpen(false)
                setGuideOpen(true)
              }}
            />
            <HelpMenuRow
              title={t(`${help}.how_it_works_title`)}
              description={t(`${help}.how_it_works_description`)}
              href={howItWorksUrl}
            />
            <HelpMenuRow
              title={t(`${help}.success_title`)}
              description={t(`${help}.success_description`)}
              href={calendlyUrl}
            />
          </div>
        ) : null}
      </div>

      <Modal isOpen={guideOpen} onOpenChange={setGuideOpen} size="2xl">
        <ModalContent>
          {({ close }) => (
            <>
              <ModalHeader onClose={close}>{t(`${guide}.title`)}</ModalHeader>
              <ModalBody className="max-h-[70vh] space-y-5 overflow-y-auto">
                <p className="text-sm text-gray-600 dark:text-gray-400">{t(`${guide}.intro`)}</p>

                <ol className="space-y-4">
                  <GuideStep number={1} title={t(`${guide}.step_connect_title`)}>
                    {t(`${guide}.step_connect_description`)}
                  </GuideStep>
                  <GuideStep number={2} title={t(`${guide}.step_template_title`)}>
                    {t(`${guide}.step_template_description`)}
                  </GuideStep>
                  <GuideStep number={3} title={t(`${guide}.step_embed_title`)}>
                    {t(`${guide}.step_embed_description`)} <InlineCode>{'</body>'}</InlineCode>.{' '}
                    {t(`${guide}.step_embed_result`)}
                  </GuideStep>
                </ol>

                <div className="rounded-lg border border-green-100 bg-green-50/50 p-3 text-sm font-medium text-green-800 dark:border-green-800/50 dark:bg-green-900/10 dark:text-green-300">
                  {t(`${guide}.important`)}
                </div>

                <AdvancedOption title={t(`${guide}.multi_title`)}>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t(`${guide}.multi_description`)}{' '}
                    {t(`${guide}.multi_step_attr_prefix`)}{' '}
                    <InlineCode>data-bonnie-venue</InlineCode>{' '}
                    {t(`${guide}.multi_step_attr_middle`)}{' '}
                    <InlineCode>data-bonnie-widget-open</InlineCode>{' '}
                    {t(`${guide}.multi_step_attr_suffix`)}
                  </p>
                  {/* The venue id is the assistant id, so the guide shows the same
                      markup the per-assistant embed code hands out. */}
                  <GuideCodeBlock>{createTriggerSnippet(EXAMPLE_VENUE_ID)}</GuideCodeBlock>
                </AdvancedOption>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
